import { DateTime, Duration } from 'luxon';
import {
  MutableRefObject,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';

type ThumbnailUrls = {
  squareDataUrl?: string;
  smallDataUrl?: string;
  originalDataUrl?: string;
};

export interface ThumbnailRendererOptions {
  videoRef: MutableRefObject<HTMLVideoElement | undefined | null>;
  onComplete?: (
    squareDataUrl: string,
    smallDataUrl: string,
    originalDataUrl: string,
    time: DateTime | Duration
  ) => void;
  interval?: number;
  renderImmediately?: boolean;
}

function compressedDataUrl(canvas: HTMLCanvasElement): string {
  //compress image using 0.5 quality
  return canvas.toDataURL('image/jpeg', 0.5);
}

export default function useThumbnailRenderer({
  videoRef,
  onComplete,
  interval,
  renderImmediately,
}: ThumbnailRendererOptions) {
  const squareCanvas = useRef(document.createElement('canvas'));
  const originalCanvas = useRef(document.createElement('canvas'));
  const smallCanvas = useRef(document.createElement('canvas'));
  const [live, setLive] = useState(false);

  const isCanvasTransparent = (
    x: number,
    y: number,
    context: CanvasRenderingContext2D | null
  ) => {
    /**
     * assumes ctx is defined
     * returns true if pixel is fully transparent
     * source: https://stackoverflow.com/a/58803433
     */
    return context?.getImageData(x, y, 1, 1).data[3] === 0; //4th byte is alpha
  };

  const drawThumbnailOnCanvas = (
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    startXCoordinate: number,
    startYCoordinate: number,
    imageWidth: number,
    imageHeight: number
  ): HTMLCanvasElement | null => {
    canvas.width = imageWidth;
    canvas.height = imageHeight;
    const ctx = canvas.getContext('2d', {
      willReadFrequently: true,
    });

    ctx?.drawImage(
      video,
      // center crop on x-axis if landscape
      startXCoordinate,
      // center crop on y-axis if portrait
      startYCoordinate,
      imageWidth,
      imageHeight,
      0,
      0,
      imageWidth,
      imageHeight
    );
    /**
     * Filter out transparent video image
     */
    if (isCanvasTransparent(startXCoordinate, startYCoordinate, ctx))
      return null;

    return canvas;
  };

  const renderThumbnail = useCallback((): ThumbnailUrls => {
    if (!videoRef.current) return {};

    const video = videoRef.current;
    const clientWidth = video.videoWidth;
    const clientHeight = video.videoHeight;

    const isLandscape = clientWidth > clientHeight;

    const xAxis = isLandscape ? (clientWidth - clientHeight) / 2 : 0;
    const yAxis = !isLandscape ? (clientHeight - clientWidth) / 2 : 0;
    // Set to a temporary value, so canvas can still render the thumbnail.
    const oSize = Math.min(clientWidth, clientHeight) || 450;

    const squareThumb = drawThumbnailOnCanvas(
      video,
      squareCanvas.current,
      xAxis,
      yAxis,
      oSize,
      oSize
    );

    const originalThumb = drawThumbnailOnCanvas(
      video,
      originalCanvas.current,
      0,
      0,
      clientWidth,
      clientHeight
    );

    if (!squareThumb || !originalThumb) return {};

    const time = isFinite(video.currentTime)
      ? Duration.fromObject({ seconds: video.currentTime })
      : DateTime.local();

    const thumbCanvas = smallCanvas.current;
    const thumbSize = 150;

    thumbCanvas.width = thumbCanvas.height = thumbSize;
    thumbCanvas
      .getContext('2d', {
        willReadFrequently: true,
      })
      ?.drawImage(squareThumb, 0, 0, thumbSize, thumbSize);

    onComplete?.(
      compressedDataUrl(squareThumb),
      compressedDataUrl(thumbCanvas),
      compressedDataUrl(originalThumb),
      time
    );

    return {
      squareDataUrl: compressedDataUrl(squareThumb),
      smallDataUrl: compressedDataUrl(thumbCanvas),
      originalDataUrl: compressedDataUrl(originalThumb),
    };
  }, [videoRef]);

  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();

  const start = () => {
    if (renderImmediately ?? true) {
      renderThumbnail();
    }

    setLive(true);
  };

  useEffect(() => {
    if (live) {
      setIntervalId(setInterval(renderThumbnail, interval ?? 5000));
    } else {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  }, [live]);

  return {
    renderThumbnail,
    startRenderingThumbnails: start,
    stopRenderingThumbnails: () => setLive(false),
  };
}
